import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { API_ENDPOINTS, API_BASE, API_PROXY_BASE } from "../config/api";
import "../css/CategoryCms.css";

const safeFetchJson = async (res) => {
    if (res.status === 401) {
        localStorage.removeItem("isAuthenticated");
        sessionStorage.removeItem("adminAuth");
        window.location.reload();
        throw new Error("Unauthorized");
    }
    if (!res.ok) {
        const text = await res.text();
        throw new Error(`Server error ${res.status}: ${text.substring(0, 100)}`);
    }
    return res.json();
};

const getAdminHeaders = () => {
    let creds = {};
    try {
        creds = JSON.parse(sessionStorage.getItem("adminAuth")) || {};
    } catch (e) {
        creds = {};
    }
    return {
        "Content-Type": "application/json",
        "x-admin-login": creds.login || "",
        "x-admin-password": creds.password || "",
    };
};

const CategoryCms = () => {
    const navigate = useNavigate();
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [companies, setCompanies] = useState([]);
    const [categories, setCategories] = useState([]);
    const [disabledCategoryIds, setDisabledCategoryIds] = useState(new Set());
    const [disabledItemIds, setDisabledItemIds] = useState(new Set());
    const [esDisabledCategoryIds, setEsDisabledCategoryIds] = useState(new Set());
    const [esDisabledItemIds, setEsDisabledItemIds] = useState(new Set());
    const [fdOnlyModifierIds, setFdOnlyModifierIds] = useState(new Set());
    const [expandedCategoryId, setExpandedCategoryId] = useState(null);
    const [expandedItemId, setExpandedItemId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const requestSeqRef = useRef(0);
    const [saving, setSaving] = useState(null);
    const [savingItem, setSavingItem] = useState(null);
    const [savingEs, setSavingEs] = useState(null);
    const [savingEsItem, setSavingEsItem] = useState(null);
    const [savingModifier, setSavingModifier] = useState(null);
    const [error, setError] = useState(null);
    const [token, setToken] = useState(null);
    const [selectedOrg, setSelectedOrg] = useState(null);
    const [externalMenus, setExternalMenus] = useState([]);

    useEffect(() => {
        const init = async () => {
            try {
                await fetch(`${API_BASE}/disabled-categories/init`, { method: "POST", headers: getAdminHeaders() });
            } catch (e) {
                // table likely already exists
            }
        };
        init();
    }, []);

    useEffect(() => {
        const fetchInitial = async () => {
            try {
                setLoading(true);

                const tokenRes = await fetch(`${API_PROXY_BASE}/access_token`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                });
                const tokenData = await safeFetchJson(tokenRes);
                setToken(tokenData.token);

                const orgRes = await fetch(`${API_PROXY_BASE}/organizations`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${tokenData.token}`,
                    },
                    body: JSON.stringify({
                        organizationIds: [],
                        returnAdditionsInfo: true,
                        includeDisabled: false,
                    }),
                });
                const orgData = await safeFetchJson(orgRes);
                const org = orgData.organizations[0];
                if (!org) return;
                setSelectedOrg(org);

                const menusRes = await fetch(`${API_PROXY_BASE}/menu`, {
                    method: "POST",
                    cache: "no-store",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${tokenData.token}`,
                    },
                    body: JSON.stringify({
                        token: tokenData.token,
                        organizationId: org.id,
                        startRevision: 0,
                    }),
                });
                const menusData = await safeFetchJson(menusRes);
                const menus = menusData.externalMenus || [];
                setExternalMenus(menus);

                const companyList = menus.map((m) => {
                    let display = m.name.replace(" Delivery", "");
                    if (m.name === "Aromiitaliani") display = "Aromi Italiani";
                    if (m.name === "CopaCabana") display = "Copacabana";
                    if (m.name === "PoshNoshs") display = "Poshnosh";
                    return { name: m.name, display, id: m.id };
                });
                setCompanies(companyList);

                if (companyList.length > 0) {
                    setSelectedCompany(companyList[0]);
                    await fetchCategories(companyList[0], org, tokenData.token, menus);
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchInitial();
    }, []);

    const fetchCategories = async (company, org, tkn, menus) => {
        const seq = ++requestSeqRef.current;
        setLoading(true);
        setError(null);
        setExpandedCategoryId(null);
        try {
            const menu = (menus || externalMenus).find(
                (m) => m.name.toLowerCase() === company.name.toLowerCase()
            );
            if (!menu) {
                if (requestSeqRef.current === seq) setCategories([]);
                return;
            }

            const menuRes = await fetch(`${API_PROXY_BASE}/menu/by_id`, {
                method: "POST",
                cache: "no-store",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${tkn || token}`,
                },
                body: JSON.stringify({
                    token: tkn || token,
                    organizationIds: [(org || selectedOrg).id],
                    externalMenuId: menu.id,
                    startRevision: 0,
                    version: 2,
                    asyncMode: false,
                }),
            });
            const menuData = await safeFetchJson(menuRes);
            const itemCategories = menuData.itemCategories || [];

            const cats = itemCategories.map((cat) => ({
                id: cat.id,
                name: cat.name,
                itemCount: (cat.items || []).length,
                items: (cat.items || []).map((item) => ({
                    id: item.itemId,
                    name: item.name,
                    price: item.itemSizes?.[0]?.prices?.[0]?.price,
                    modifiers: (item.itemSizes?.[0]?.itemModifierGroups || []).flatMap(
                        (group) => group.items.map((mod) => ({
                            id: mod.itemId,
                            name: mod.name,
                            groupName: group.name,
                        }))
                    ),
                })),
            }));

            const orgId = company.name.toLowerCase();
            const [disabledResult, disabledItemsResult, esDisabledResult, esDisabledItemsResult, fdModResult] = await Promise.allSettled([
                fetch(API_ENDPOINTS.disabledCategories.get(orgId, 'fd')).then(safeFetchJson),
                fetch(API_ENDPOINTS.disabledItems.get(orgId, 'fd')).then(safeFetchJson),
                fetch(API_ENDPOINTS.disabledCategories.get(orgId, 'es')).then(safeFetchJson),
                fetch(API_ENDPOINTS.disabledItems.get(orgId, 'es')).then(safeFetchJson),
                fetch(API_ENDPOINTS.fdOnlyModifiers.get(orgId)).then(safeFetchJson),
            ]);
            if (requestSeqRef.current !== seq) return;
            const disabledData = disabledResult.status === "fulfilled" ? disabledResult.value : {};
            const disabledItemsData = disabledItemsResult.status === "fulfilled" ? disabledItemsResult.value : {};
            const esDisabledData = esDisabledResult.status === "fulfilled" ? esDisabledResult.value : {};
            const esDisabledItemsData = esDisabledItemsResult.status === "fulfilled" ? esDisabledItemsResult.value : {};
            const fdModData = fdModResult.status === "fulfilled" ? fdModResult.value : {};
            setCategories(cats);
            setDisabledCategoryIds(new Set(
                (disabledData.disabledCategories || []).map((d) => d.category_id)
            ));
            setDisabledItemIds(new Set(
                (disabledItemsData.disabledItems || []).map((d) => d.item_id)
            ));
            setEsDisabledCategoryIds(new Set(
                (esDisabledData.disabledCategories || []).map((d) => d.category_id)
            ));
            setEsDisabledItemIds(new Set(
                (esDisabledItemsData.disabledItems || []).map((d) => d.item_id)
            ));
            setFdOnlyModifierIds(new Set(
                (fdModData.modifiers || []).map((m) => m.modifier_id)
            ));
        } catch (err) {
            if (requestSeqRef.current === seq) setError(err.message);
        } finally {
            if (requestSeqRef.current === seq) setLoading(false);
        }
    };

    const handleCompanySelect = async (company) => {
        setSelectedCompany(company);
        await fetchCategories(company);
    };

    const handleRefresh = async () => {
        if (!selectedCompany) return;
        setRefreshing(true);
        try {
            await fetchCategories(selectedCompany, selectedOrg, token, externalMenus);
        } finally {
            setRefreshing(false);
        }
    };

    const handleToggle = async (categoryId, categoryName) => {
        const isCurrentlyDisabled = disabledCategoryIds.has(categoryId);
        const newDisabled = !isCurrentlyDisabled;

        setSaving(categoryId);
        try {
            const orgId = selectedCompany.name.toLowerCase();
            const res = await fetch(API_ENDPOINTS.disabledCategories.toggle, {
                method: "POST",
                headers: getAdminHeaders(),
                body: JSON.stringify({
                    orgId,
                    categoryId,
                    categoryName,
                    disabled: newDisabled,
                    site: "fd",
                }),
            });
            const data = await safeFetchJson(res);
            if (data.success) {
                const newDisabledIds = new Set(
                    data.disabledCategories.map((d) => d.category_id)
                );
                setDisabledCategoryIds(newDisabledIds);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(null);
        }
    };

    const handleEsToggle = async (categoryId, categoryName) => {
        const isCurrentlyDisabled = esDisabledCategoryIds.has(categoryId);
        const newDisabled = !isCurrentlyDisabled;

        setSavingEs(categoryId);
        try {
            const orgId = selectedCompany.name.toLowerCase();
            const res = await fetch(API_ENDPOINTS.disabledCategories.toggle, {
                method: "POST",
                headers: getAdminHeaders(),
                body: JSON.stringify({
                    orgId,
                    categoryId,
                    categoryName,
                    disabled: newDisabled,
                    site: "es",
                }),
            });
            const data = await safeFetchJson(res);
            if (data.success) {
                const newDisabledIds = new Set(
                    data.disabledCategories.map((d) => d.category_id)
                );
                setEsDisabledCategoryIds(newDisabledIds);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setSavingEs(null);
        }
    };

    const handleItemToggle = async (itemId, itemName) => {
        const isCurrentlyDisabled = disabledItemIds.has(itemId);
        const newDisabled = !isCurrentlyDisabled;

        setSavingItem(itemId);
        try {
            const orgId = selectedCompany.name.toLowerCase();
            const res = await fetch(API_ENDPOINTS.disabledItems.toggle, {
                method: "POST",
                headers: getAdminHeaders(),
                body: JSON.stringify({
                    orgId,
                    itemId,
                    itemName,
                    disabled: newDisabled,
                    site: "fd",
                }),
            });
            const data = await safeFetchJson(res);
            if (data.success) {
                const newDisabledIds = new Set(
                    data.disabledItems.map((d) => d.item_id)
                );
                setDisabledItemIds(newDisabledIds);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setSavingItem(null);
        }
    };

    const handleEsItemToggle = async (itemId, itemName) => {
        const isCurrentlyDisabled = esDisabledItemIds.has(itemId);
        const newDisabled = !isCurrentlyDisabled;

        setSavingEsItem(itemId);
        try {
            const orgId = selectedCompany.name.toLowerCase();
            const res = await fetch(API_ENDPOINTS.disabledItems.toggle, {
                method: "POST",
                headers: getAdminHeaders(),
                body: JSON.stringify({
                    orgId,
                    itemId,
                    itemName,
                    disabled: newDisabled,
                    site: "es",
                }),
            });
            const data = await safeFetchJson(res);
            if (data.success) {
                const newDisabledIds = new Set(
                    data.disabledItems.map((d) => d.item_id)
                );
                setEsDisabledItemIds(newDisabledIds);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setSavingEsItem(null);
        }
    };

    const handleModifierToggle = async (modifierId, modifierName) => {
        const isCurrentlyRestricted = fdOnlyModifierIds.has(modifierId);
        const newRestricted = !isCurrentlyRestricted;

        setSavingModifier(modifierId);
        try {
            const orgId = selectedCompany.name.toLowerCase();
            const res = await fetch(API_ENDPOINTS.fdOnlyModifiers.toggle, {
                method: "POST",
                headers: getAdminHeaders(),
                body: JSON.stringify({
                    orgId,
                    modifierId,
                    modifierName,
                    restricted: newRestricted,
                }),
            });
            const data = await safeFetchJson(res);
            if (data.success) {
                const newIds = new Set(
                    data.modifiers.map((m) => m.modifier_id)
                );
                setFdOnlyModifierIds(newIds);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setSavingModifier(null);
        }
    };

    const toggleExpand = (categoryId) => {
        setExpandedCategoryId(expandedCategoryId === categoryId ? null : categoryId);
        setExpandedItemId(null);
    };

    const toggleItemExpand = (itemId) => {
        setExpandedItemId(expandedItemId === itemId ? null : itemId);
    };

    return (
        <div className="category-cms-container">
            <div className="cms-top-bar">
                <button className="cms-back-btn" onClick={() => navigate(-1)}>
                    Back
                </button>
                <h1 className="cms-title">Menu Management</h1>
                <button
                    className="cms-refresh-btn"
                    onClick={handleRefresh}
                    disabled={loading || !selectedCompany}
                >
                    {refreshing ? "Refreshing..." : "Refresh menu"}
                </button>
            </div>

            <div className="cms-company-tabs">
                {companies.map((company) => (
                    <button
                        key={company.name}
                        className={`cms-tab-btn ${selectedCompany?.name === company.name ? "active" : ""}`}
                        onClick={() => handleCompanySelect(company)}
                    >
                        {company.display}
                    </button>
                ))}
            </div>

            {error && <div className="cms-error">{error}</div>}

            {loading ? (
                <div className="cms-loading">Loading menu...</div>
            ) : (
                <div className="cms-categories-list">
                    {categories.length === 0 && (
                        <div className="cms-empty">No categories found for {selectedCompany?.display}</div>
                    )}
                    {categories.map((cat) => {
                        const isFdDisabled = disabledCategoryIds.has(cat.id);
                        const isEsDisabled = esDisabledCategoryIds.has(cat.id);
                        const isFdSaving = saving === cat.id;
                        const isEsSaving = savingEs === cat.id;
                        const isExpanded = expandedCategoryId === cat.id;
                        return (
                            <div key={cat.id} className="cms-category-block">
                                <div className={`cms-category-row ${isFdDisabled && isEsDisabled ? "disabled" : ""}`}>
                                    <div className="cms-category-info" onClick={() => toggleExpand(cat.id)}>
                                        <div className="cms-category-name-row">
                                            <span className={`cms-expand-arrow ${isExpanded ? "expanded" : ""}`}>&#9654;</span>
                                            <span className="cms-category-name">{cat.name}</span>
                                        </div>
                                        <span className="cms-category-count">{cat.itemCount} items</span>
                                    </div>
                                    <div className="cms-category-controls">
                                        <div className="cms-toggle-group" onClick={(e) => e.stopPropagation()}>
                                            <span className="cms-toggle-label">FD</span>
                                            <label className="cms-toggle">
                                                <input
                                                    type="checkbox"
                                                    checked={!isFdDisabled}
                                                    onChange={() => handleToggle(cat.id, cat.name)}
                                                    disabled={isFdSaving}
                                                />
                                                <span className="cms-toggle-slider"></span>
                                            </label>
                                        </div>
                                        <div className="cms-toggle-group" onClick={(e) => e.stopPropagation()}>
                                            <span className="cms-toggle-label cms-toggle-label-es">ES</span>
                                            <label className="cms-toggle">
                                                <input
                                                    type="checkbox"
                                                    checked={!isEsDisabled}
                                                    onChange={() => handleEsToggle(cat.id, cat.name)}
                                                    disabled={isEsSaving}
                                                />
                                                <span className="cms-toggle-slider cms-toggle-slider-es"></span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                {isExpanded && cat.items && (
                                    <div className="cms-items-list">
                                        {cat.items.map((item) => {
                                            const isFdItemDisabled = disabledItemIds.has(item.id);
                                            const isEsItemDisabled = esDisabledItemIds.has(item.id);
                                            const isFdItemSaving = savingItem === item.id;
                                            const isEsItemSaving = savingEsItem === item.id;
                                            const hasModifiers = item.modifiers && item.modifiers.length > 0;
                                            const isItemExpanded = expandedItemId === item.id;
                                            return (
                                                <div key={item.id} className="cms-item-block">
                                                    <div className={`cms-item-row ${isFdItemDisabled && isEsItemDisabled ? "disabled" : ""}`}>
                                                        <div className="cms-item-info" onClick={() => hasModifiers && toggleItemExpand(item.id)} style={hasModifiers ? { cursor: "pointer" } : {}}>
                                                            {hasModifiers && (
                                                                <span className={`cms-expand-arrow cms-expand-arrow-small ${isItemExpanded ? "expanded" : ""}`}>&#9654;</span>
                                                            )}
                                                            <span className="cms-item-name">{item.name}</span>
                                                            {item.price != null && (
                                                                <span className="cms-item-price">{item.price} ₾</span>
                                                            )}
                                                        </div>
                                                        <div className="cms-category-controls">
                                                            <div className="cms-toggle-group">
                                                                <span className="cms-toggle-label">FD</span>
                                                                <label className="cms-toggle cms-toggle-small">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={!isFdItemDisabled}
                                                                        onChange={() => handleItemToggle(item.id, item.name)}
                                                                        disabled={isFdItemSaving}
                                                                    />
                                                                    <span className="cms-toggle-slider"></span>
                                                                </label>
                                                            </div>
                                                            <div className="cms-toggle-group">
                                                                <span className="cms-toggle-label cms-toggle-label-es">ES</span>
                                                                <label className="cms-toggle cms-toggle-small">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={!isEsItemDisabled}
                                                                        onChange={() => handleEsItemToggle(item.id, item.name)}
                                                                        disabled={isEsItemSaving}
                                                                    />
                                                                    <span className="cms-toggle-slider cms-toggle-slider-es"></span>
                                                                </label>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {isItemExpanded && hasModifiers && (
                                                        <div className="cms-modifiers-list">
                                                            {item.modifiers.map((mod) => {
                                                                const isFdOnly = fdOnlyModifierIds.has(mod.id);
                                                                const isModSaving = savingModifier === mod.id;
                                                                return (
                                                                    <div key={mod.id} className={`cms-modifier-row ${isFdOnly ? "fd-only" : ""}`}>
                                                                        <div className="cms-modifier-info">
                                                                            <span className="cms-modifier-name">{mod.name}</span>
                                                                        </div>
                                                                        <div className="cms-category-controls">
                                                                            <label className="cms-toggle cms-toggle-small">
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={isFdOnly}
                                                                                    onChange={() => handleModifierToggle(mod.id, mod.name)}
                                                                                    disabled={isModSaving}
                                                                                />
                                                                                <span className="cms-toggle-slider cms-toggle-slider-fd"></span>
                                                                            </label>
                                                                            <span className={`cms-status ${isFdOnly ? "fd" : "off"}`}>
                                                                                {isModSaving ? "Saving..." : isFdOnly ? "FD Only" : "All"}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default CategoryCms;
